/**
 * cw-main-product.js
 * Handles: gallery slides/thumbs, variant selection, qty stepper, ATC feedback
 */

(function () {
  'use strict';

  /* ────────────────────────────────────────────────────────────
     Utility helpers
  ──────────────────────────────────────────────────────────── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const formatMoney = (cents) =>
    (window.Shopify && Shopify.formatMoney)
      ? Shopify.formatMoney(cents, window.theme?.moneyFormat || '£{{amount}}')
      : `£${(cents / 100).toFixed(2)}`;

  /* ────────────────────────────────────────────────────────────
     Gallery
  ──────────────────────────────────────────────────────────── */
  function initGallery(section) {
    const gallery = $('[data-gallery]', section);
    if (!gallery) return;

    const slides = $$('[data-gallery-slide]', gallery);
    const thumbs = $$('[data-gallery-thumb]', gallery);
    const counter = $('[data-gallery-current]', gallery);
    let current = 0;

    function goTo(index) {
      slides[current] && slides[current].classList.remove('is-active');
      thumbs[current] && thumbs[current].classList.remove('is-active');
      thumbs[current] && thumbs[current].setAttribute('aria-selected', 'false');

      current = (index + slides.length) % slides.length;

      slides[current] && slides[current].classList.add('is-active');
      thumbs[current] && thumbs[current].classList.add('is-active');
      thumbs[current] && thumbs[current].setAttribute('aria-selected', 'true');

      if (counter) counter.textContent = current + 1;
    }

    on($('[data-gallery-prev]', gallery), 'click', () => goTo(current - 1));
    on($('[data-gallery-next]', gallery), 'click', () => goTo(current + 1));

    thumbs.forEach(function(thumb, i) { on(thumb, 'click', () => goTo(i)); });

    // Swipe support
    var startX = 0;
    on(gallery, 'touchstart', function(e) { startX = e.touches[0].clientX; }, { passive: true });
    on(gallery, 'touchend', function(e) {
      var diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) goTo(diff > 0 ? current + 1 : current - 1);
    });
  }

  /* ────────────────────────────────────────────────────────────
     Quantity Stepper
  ──────────────────────────────────────────────────────────── */
  function initQty(section) {
    const wrapper = $('[data-qty-wrapper]', section);
    if (!wrapper) return;

    const input = $('[data-qty-input]', wrapper);
    const minus = $('[data-qty-minus]', wrapper);
    const plus  = $('[data-qty-plus]', wrapper);

    const clamp = (v) => Math.max(1, parseInt(v, 10) || 1);

    on(minus, 'click', function() { input.value = clamp(input.value) - 1 || 1; });
    on(plus,  'click', function() { input.value = clamp(input.value) + 1; });
    on(input, 'change', function() { input.value = clamp(input.value); });
    on(input, 'blur',   function() { input.value = clamp(input.value); });
  }

  /* ────────────────────────────────────────────────────────────
     Variant Selector
  ──────────────────────────────────────────────────────────── */
  function initVariants(section) {
    const selector = $('[data-variant-selector]', section);
    if (!selector) return;

    const nativeSelect = $('[data-variant-select]', selector);
    if (!nativeSelect) return;

    const optionBtns = $$('[data-option-value]', selector);
    const formInput  = $('[data-product-form-input]', section);
    const priceEl    = $('[data-product-price]', section);
    const compareEl  = $('[data-compare-price]', section);
    const skuEl      = $('[data-variant-sku]', section);
    const atcBtn     = $('[data-atc-btn]', section);
    const atcText    = $('[data-atc-text]', section);

    const state = {};
    $$('[data-option-position]', selector).forEach(function(btn) {
      if (btn.classList.contains('is-selected')) {
        state[btn.dataset.optionPosition] = btn.dataset.optionValue;
      }
    });

    function updateSelectedLabels() {
      $$('[data-selected-option]', selector).forEach(function(span) {
        var name = span.dataset.selectedOption;
        var group = selector.querySelector('[data-option-name="' + name + '"]');
        var btn = group && $('[data-option-value].is-selected', group);
        if (btn) span.textContent = btn.dataset.optionValue;
      });
    }

    function getVariantByOptions() {
      var selectedTitles = [];
      $$('[data-option-position]', selector).forEach(function(btn) {
        if (btn.classList.contains('is-selected')) {
          selectedTitles.push(btn.dataset.optionValue);
        }
      });
      var joined = selectedTitles.join(' / ');
      for (var i = 0; i < nativeSelect.options.length; i++) {
        var opt = nativeSelect.options[i];
        if (opt.text.trim() === joined) return { id: opt.value, dataset: opt.dataset };
      }
      return null;
    }

    function syncNativeSelect() {
      var variant = getVariantByOptions();
      if (!variant) return;
      for (var i = 0; i < nativeSelect.options.length; i++) {
        if (nativeSelect.options[i].value === String(variant.id)) {
          nativeSelect.selectedIndex = i;
          break;
        }
      }
    }

    function onOptionClick(btn) {
      var pos = btn.dataset.optionPosition;
      var val = btn.dataset.optionValue;

      $$('[data-option-position="' + pos + '"]', selector).forEach(function(b) {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });

      btn.classList.add('is-selected');
      btn.setAttribute('aria-pressed', 'true');

      state[pos] = val;
      updateSelectedLabels();
      syncNativeSelect();

      var matchOpt = nativeSelect.options[nativeSelect.selectedIndex];
      if (!matchOpt) return;

      var price        = matchOpt.dataset.price;
      var comparePrice = matchOpt.dataset.comparePrice;
      var sku          = matchOpt.dataset.sku;
      var available    = matchOpt.dataset.available === 'true';
      var variantId    = matchOpt.value;

      if (priceEl && price) priceEl.textContent = formatMoney(parseInt(price, 10));
      if (compareEl) {
        if (comparePrice && parseInt(comparePrice, 10) > parseInt(price, 10)) {
          compareEl.textContent = formatMoney(parseInt(comparePrice, 10));
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      if (skuEl) skuEl.textContent = sku ? 'SKU: ' + sku : '';
      if (formInput) formInput.value = variantId;

      if (atcBtn) {
        atcBtn.disabled = !available;
        atcBtn.setAttribute('aria-disabled', String(!available));
        atcBtn.classList.toggle('cw-atc-btn--sold-out', !available);
      }
      if (atcText) {
        atcText.textContent = available
          ? (window.cartStrings && window.cartStrings.addToCart || 'ADD TO CART')
          : (window.cartStrings && window.cartStrings.soldOut   || 'SOLD OUT');
      }

      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        // silent — URL com caracteres especiais no Safari
      }

      section.dispatchEvent(new CustomEvent('cw:variant:changed', {
        bubbles: true,
        detail: { variantId: variantId, available: available, price: price, comparePrice: comparePrice, sku: sku }
      }));
    }

    optionBtns.forEach(function(btn) { on(btn, 'click', function() { onOptionClick(btn); }); });
  }

  /* ────────────────────────────────────────────────────────────
     ATC Form — AJAX
  ──────────────────────────────────────────────────────────── */
  function initATC(section) {
    const form = $('[data-type="add-to-cart-form"]', section);
    if (!form) return;

    const btn     = $('[data-atc-btn]', form);
    const text    = $('[data-atc-text]', form);
    const spinner = $('[data-atc-btn] .cw-atc-btn__spinner', form);

    // Produto sold out — não inicializa o listener de submit
    if (btn && btn.disabled) return;

    on(form, 'submit', function(e) {
      e.preventDefault();

      var formData = new FormData(form);
      var body = JSON.stringify({
        items: [{ id: formData.get('id'), quantity: parseInt(formData.get('quantity'), 10) || 1 }]
      });

      btn.disabled = true;
      if (spinner) spinner.hidden = false;
      if (text) text.textContent = '...';

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      fetch(root + 'cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    body
      })
      .then(function(res) {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then(function(data) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cw:cart:added', { bubbles: true, detail: data }));

        if (text) text.textContent = (window.cartStrings && window.cartStrings.itemAdded) || 'ADDED!';

        setTimeout(function() {
          if (text) text.textContent = (window.cartStrings && window.cartStrings.addToCart) || 'ADD TO CART';
          btn.disabled = false;
          if (spinner) spinner.hidden = true;
        }, 1500);
      })
      .catch(function(err) {
        console.error('[CW ATC]', err);
        if (text) text.textContent = 'Error – try again';
        btn.disabled = false;
        if (spinner) spinner.hidden = true;
      });
    });
  }

  /* ────────────────────────────────────────────────────────────
     Accordion
  ──────────────────────────────────────────────────────────── */
  function initAccordions(section) {
    var accordions = $$('.cw-accordion', section);
    accordions.forEach(function(acc) {
      var summary = $('summary', acc);
      on(summary, 'click', function() {});
    });
  }

  /* ────────────────────────────────────────────────────────────
     Bootstrap
  ──────────────────────────────────────────────────────────── */
  function init() {
    $$('[data-section-type="cw-main-product"]').forEach(function(section) {
      initGallery(section);
      initQty(section);
      initVariants(section);
      initATC(section);
      initAccordions(section);
    });
  }

  document.addEventListener('shopify:section:load', function(e) {
    if (e.target.dataset.sectionType === 'cw-main-product') {
      initGallery(e.target);
      initQty(e.target);
      initVariants(e.target);
      initATC(e.target);
      initAccordions(e.target);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();